import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import EmailVerification from '../components/EmailVerification';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const VoteWithToken = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [hasAuthenticated, setHasAuthenticated] = useState(false);

  const authenticateWithToken = async () => {
    try {
      setLoading(true);
      setError(null);
      setHasAuthenticated(true); // Prevent duplicate calls

      const response = await axios.post(`${API_URL}/voting-auth/authenticate`, {
        token,
        ipAddress: null, // Will be captured by backend
        userAgent: navigator.userAgent,
      });

      if (response.data.success) {
        const { sessionId, userId, electionId, requiresVerification } = response.data;

        setSessionData({ sessionId, userId, electionId });

        if (requiresVerification) {
          // Requires email verification
          setRequiresVerification(true);
          setLoading(false);
        } else {
          // Redirect to voting page with session
          navigate(`/elections/${electionId}/vote?sessionId=${sessionId}`);
        }
      }
    } catch (err) {
      console.error('Authentication error:', err);
      setError(
        err.response?.data?.message || 
        'Invalid or expired voting link. Please contact the election administrator.'
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasAuthenticated) {
      authenticateWithToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, hasAuthenticated]);

  const handleVerificationComplete = (sessionId, electionId) => {
    // Redirect to voting page after verification
    const finalElectionId = electionId || sessionData?.electionId;
    navigate(`/elections/${finalElectionId}/vote?sessionId=${sessionId}`);
  };

  if (loading) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '3rem' }}>
        <div className="card shadow-sm">
          <div className="card-body text-center p-5">
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5 className="mb-2">Authenticating your voting link...</h5>
            <p className="text-muted mb-0">Please wait while we verify your access.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '3rem' }}>
        <div className="card shadow-sm">
          <div className="card-body p-4">
            <div className="alert alert-danger mb-0">
              <h5 className="alert-heading">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Authentication Failed
              </h5>
              <p className="mb-0">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (requiresVerification) {
    return (
      <div className="container" style={{ maxWidth: '600px', marginTop: '3rem' }}>
        <EmailVerification
          sessionId={sessionData.sessionId}
          onVerificationComplete={handleVerificationComplete}
        />
      </div>
    );
  }

  return null;
};

export default VoteWithToken;
